import React, { useEffect, useState } from 'react'
import { Alert, Col, Form, Input, InputNumber, Modal, Row, Select, Spin, message } from 'antd'
import { getCatalogItem, saveCatalogItem } from './api'
import {
    AUDIENCE_OPTIONS, CODE_PATTERN, ENTRY_TYPE_OPTIONS, KIND_OPTIONS, NEW_ITEM, STATUS_OPTIONS,
    buildItemPayload, jsonArrayToLines,
} from './model'

const { TextArea } = Input

const SCENES_HELP = '每行一条，保存时自动转成 JSON 数组字符串；CLI 按行渲染为场景列表。'

// 2026-10-22 coder(lq): 编辑弹窗独立加载详情，避免列表行数据过期；编码是稳定标识，编辑态只读。
export default function ItemEditorModal({ open, itemId, onCancel, onSaved }) {
    const [form] = Form.useForm()
    const creating = !itemId
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [enabled, setEnabled] = useState(1)

    useEffect(() => {
        if (!open) return undefined
        let active = true
        setLoadError('')
        form.resetFields()
        if (!itemId) {
            setEnabled(1)
            form.setFieldsValue(NEW_ITEM)
            return () => { active = false }
        }
        setLoading(true)
        getCatalogItem(itemId).then(item => {
            if (!active) return
            if (!item) {
                setLoadError('未读取到该能力条目，可能已被删除，请刷新列表后重试')
                return
            }
            setEnabled(Number(item.enabled) === 0 ? 0 : 1)
            form.setFieldsValue({
                ...NEW_ITEM,
                ...item,
                sortOrder: Number(item.sortOrder) || 0,
                scenes: jsonArrayToLines(item.scenes),
                notFor: jsonArrayToLines(item.notFor),
            })
        }).catch(error => {
            if (active) setLoadError(error && error.message ? error.message : '能力条目加载失败')
        }).finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [open, itemId, form])

    const submit = async () => {
        let values
        try {
            values = await form.validateFields()
        } catch (error) {
            return
        }
        setSaving(true)
        try {
            await saveCatalogItem(buildItemPayload(values, { id: itemId || undefined, enabled }))
            message.success('已保存')
            onSaved()
        } catch (error) {
            // api.js 已统一 message.error，这里只负责结束 loading、保持弹窗打开供修正。
        } finally {
            setSaving(false)
        }
    }

    return <Modal
        open={open}
        title={creating ? '新增能力条目' : '编辑能力条目'}
        width={780}
        onCancel={onCancel}
        onOk={submit}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        maskClosable={false}
        okButtonProps={{ disabled: loading || !!loadError }}
    >
        {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 16 }} />}
        {!creating && !loadError && <Alert type="info" showIcon message="编码是 CLI 与文档引用条目的稳定标识，保存后不可修改；停用请在列表操作列处理。"
            style={{ marginBottom: 16 }} />}
        <Spin spinning={loading}>
            <Form form={form} layout="vertical" initialValues={NEW_ITEM}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="code" label="编码" rules={[
                            { required: true, message: '请填写编码' },
                            { pattern: CODE_PATTERN, message: '编码限字母、数字、下划线或短横线，且不能以符号开头，最长 64 位' },
                        ]} extra={creating ? '示例：aic-data-asset，保存后不可修改' : '编码保存后不可修改'}>
                            <Input maxLength={64} disabled={!creating} placeholder="aic-data-asset" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="name" label="名称" rules={[{ required: true, message: '请填写名称' }]}>
                            <Input maxLength={128} placeholder="AIC 数据资产" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="kind" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
                            <Select options={KIND_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="audience" label="受众" rules={[{ required: true, message: '请选择受众' }]}>
                            <Select options={AUDIENCE_OPTIONS} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                            <Select options={STATUS_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="sortOrder" label="排序" extra="越小越靠前">
                            <InputNumber min={0} max={999999} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="entryType" label="入口类型">
                            <Select allowClear options={ENTRY_TYPE_OPTIONS} placeholder="cli / skill / web / api" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="entryValue" label="入口值" extra="命令、skill 名或 URL">
                            <Input maxLength={512} placeholder="aic-data-asset" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="owner" label="负责人 / 负责部门">
                            <Input maxLength={64} placeholder="数据中心" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="docTopic" label="关联文档 topic" extra="对应「文档」页签左侧的 topic">
                            <Input maxLength={64} placeholder="aic" />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="summary" label="一句话说明">
                    <Input maxLength={512} placeholder="线下数据的登记、上传、归档与数据字典维护" />
                </Form.Item>
                <Form.Item name="scenes" label="适用场景" extra={SCENES_HELP}>
                    <TextArea rows={3} maxLength={4000} placeholder={'要上传线下 Excel\n要查某业务数据'} />
                </Form.Item>
                <Form.Item name="notFor" label="不适用场景" extra="每行一条，用于说明什么时候不要用这个能力，防止误用。">
                    <TextArea rows={3} maxLength={4000} placeholder={'有自研系统要部署（请用 Vibe）'} />
                </Form.Item>
                <Form.Item name="remark" label="备注">
                    <TextArea rows={2} maxLength={512} placeholder="仅后台可见的补充说明" />
                </Form.Item>
            </Form>
        </Spin>
    </Modal>
}
